// src/pages/fleet/master/AssetMasterPage.tsx — GPS device (asset) master (sample data for now).
import React, { useState } from 'react';
import MasterTable, { Pill, FormValues } from './MasterTable';
import { SAMPLE_ASSETS, GPS_PROVIDERS, AssetRow } from './sampleData';

const PROVIDER_TINT: Record<string, string> = { Wheelseye: 'var(--fx-accent)', Margogps: 'var(--fx-purple)', Blackbuck: '#0F172A' };

export default function AssetMasterPage() {
  const [rows, setRows] = useState<AssetRow[]>(SAMPLE_ASSETS);

  const onSave = async (form: FormValues, existing: AssetRow | null) => {
    if (!/^\d{15}$/.test(String(form.imei).trim())) throw new Error('IMEI must be exactly 15 digits');
    if (rows.some((r) => r.imei === form.imei && r.id !== existing?.id)) throw new Error('This IMEI is already registered');
    const rec = {
      imei: String(form.imei).trim(), provider: String(form.provider), sim: String(form.sim ?? ''),
      installedOn: String(form.installedOn ?? ''),
      vehicleNumber: String(form.vehicleNumber || '').toUpperCase().replace(/\s+/g, ''), active: form.active !== false,
    };
    setRows((rs) => (existing ? rs.map((r) => (r.id === existing.id ? { ...r, ...rec } : r)) : [...rs, { ...rec, id: `a${Date.now()}`, lastSeen: 'never' }]));
  };
  const onToggle = (r: AssetRow) => setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, active: !x.active } : x)));

  return (
    <MasterTable<AssetRow>
      icon="antenna" title="Assets" subtitle="GPS devices, their provider and the vehicle they are fitted on"
      sample rows={rows} addLabel="Asset"
      searchKeys={['imei', 'vehicleNumber', 'sim', 'provider']}
      filters={[{ key: 'provider', label: 'Provider', options: GPS_PROVIDERS }]}
      stats={[
        { label: 'Total Devices', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'Mapped to vehicle', value: rows.filter((r) => r.vehicleNumber).length, tint: 'var(--fx-green)' },
        { label: 'Unmapped', value: rows.filter((r) => !r.vehicleNumber).length, tint: 'var(--fx-orange)' },
        { label: 'Inactive', value: rows.filter((r) => !r.active).length, tint: 'var(--fx-text-3)' },
      ]}
      columns={[
        { key: 'imei', label: 'Device IMEI', render: (r) => <b style={{ fontFamily: 'DM Mono, monospace' }}>{r.imei}</b> },
        { key: 'provider', label: 'Provider', render: (r) => (
          <span style={{ fontSize: 11, fontWeight: 600, color: PROVIDER_TINT[r.provider] || '#8E8E93', background: `${PROVIDER_TINT[r.provider] || '#8E8E93'}14`, padding: '3px 9px', borderRadius: 20 }}>{r.provider}</span>
        ) },
        { key: 'vehicleNumber', label: 'Vehicle', render: (r) => (r.vehicleNumber ? <b>{r.vehicleNumber}</b> : <span style={{ color: 'var(--fx-orange)', fontWeight: 600 }}>Not mapped</span>) },
        { key: 'sim', label: 'SIM No.' },
        { key: 'installedOn', label: 'Installed On' },
        { key: 'lastSeen', label: 'Last Signal', sortable: false },
        { key: 'active', label: 'Status', render: (r) => <Pill on={r.active} />, csvValue: (r) => (r.active ? 'Active' : 'Inactive') },
      ]}
      fields={[
        { key: 'imei', label: 'Device IMEI', required: true, placeholder: '15-digit IMEI', lockOnEdit: true },
        { key: 'provider', label: 'GPS Provider', type: 'select', required: true, options: GPS_PROVIDERS },
        { key: 'vehicleNumber', label: 'Mapped Vehicle', placeholder: 'e.g. AP16W1415', hint: 'Leave empty if the device is not fitted yet' },
        { key: 'sim', label: 'SIM Number', placeholder: 'SIM ICCID / number' },
        { key: 'installedOn', label: 'Installed On', type: 'date' },
        { key: 'active', label: 'Status', type: 'toggle', default: true },
      ]}
      onSave={onSave} onToggle={onToggle}
    />
  );
}
